import {useState, useRef, useEffect} from 'preact/hooks'
import {IconLocationPin} from './svg-icons/IconLocationPin.tsx'
import {IconCloseBig} from "./svg-icons/IconCloseBig.tsx";
import '../styles/components/DestinationSearchV2.css';
import {ExtIdItem, FilterOption, KuratierteReiseItem, SearchRedirect} from "../types/travel.ts";

interface DestinationSearchV2Props {
    options?: FilterOption[]
    selectedValues: string[]
    onSelectionChange: (values: string[]) => void
    onTriggerWidgetSearchClick?: () => void
    placeholder?: string
    label?: string
    cssClasses?: string
    extIdItems?: ExtIdItem[]
    kuratierteReisen?: KuratierteReiseItem[]
    searchRedirects?: SearchRedirect[]
    isSearchWidget?: boolean
}

export function DestinationSearchV2({
                                        options,
                                        selectedValues,
                                        onSelectionChange,
                                        onTriggerWidgetSearchClick,
                                        placeholder = "Wohin geht's?",
                                        label = "REISEZIEL / REISECODE",
                                        cssClasses = '',
                                        kuratierteReisen,
                                        searchRedirects,
                                        extIdItems,
                                        isSearchWidget = false,
                                    }: DestinationSearchV2Props) {

    const [isOpen, setIsOpen] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showPlaceholder, setShowPlaceholder] = useState(true)
    const [showEmptyFieldButton, setShowEmptyFieldButton] = useState(false)
    const [activeIndex, setActiveIndex] = useState(-1)

    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const spanRef = useRef<HTMLSpanElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const activeItemRef = useRef<HTMLButtonElement>(null)

    // Helper function to normalize strings by removing diacritics
    const normalizeString = (str: string): string => {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    const filteredExtIdItems = extIdItems?.filter(extid => extid.extid.toLowerCase() === searchTerm.toLowerCase())

    const filteredOptions = options?.filter(option => {
        const normalizedOption = normalizeString(option?.value || '');
        const normalizedSearch = normalizeString(searchTerm || '');
        return normalizedOption.startsWith(normalizedSearch) &&
            !selectedValues.includes(option?.value);
    }) || []

    console.debug(searchTerm)

    let maxSelectedOptionItems = 4;
    let maxFilteredOptionItems = filteredOptions.length;
    if(isSearchWidget) {
        maxSelectedOptionItems = 1
        maxFilteredOptionItems = 8
    }

    const selectedOptions = options?.filter(option =>
        selectedValues.includes(option.value)
    ) || []

    const handleOptionSelect = (value: string) => {
        if (!selectedValues.includes(value)) {
            onSelectionChange([...selectedValues, value])
        }
        setSearchTerm('')
        setActiveIndex(-1)
        setIsOpen(false)
        setShowEmptyFieldButton(false);

        setTimeout(() => {
            inputRef.current?.focus()
        }, 200)
    }

    const handleOptionRemove = (value: string) => {
        onSelectionChange(selectedValues.filter(v => v !== value))
        setIsOpen(false);

        if(isSearchWidget) {
            setShowPlaceholder(false);
            setTimeout(() => {
                inputRef.current?.focus()
            }, 200)
        } else {
            setShowPlaceholder(true);
        }
    }

    const handleFocusChange = (e: Event, selectedOptions: FilterOption[]) => {
        const target = e.target as HTMLInputElement

        if(!isSearchWidget) {
            setIsFocused(false)
            setIsTyping(true)
            return false;
        }

        if(selectedOptions.length === 0) {
            if(!kuratierteReisen || kuratierteReisen.length === 0) {
                setIsFocused(false)
                setIsTyping(true)
                setIsOpen(true)
            } else if(target.value.length === 0) {
                setIsFocused(true)
                setIsTyping(false)
                setIsOpen(true)
            }
        } else if(selectedOptions.length >= maxSelectedOptionItems){
            setIsFocused(false)
            setIsTyping(false)
            setIsOpen(false)
        } else {
            setIsFocused(false)
            setIsTyping(true)
        }
    }

    const handleEnterAction = (value: string) => {


        if(selectedOptions.length >= maxSelectedOptionItems) {
            if(isSearchWidget && onTriggerWidgetSearchClick) {
                onTriggerWidgetSearchClick()
            }
            return;
        } else {
            if(value.length === 0) {
                return;
            }
        }

        const filteredSearchRedirects = searchRedirects?.filter(searchRedirect =>
            searchRedirect?.term?.toLowerCase() === searchTerm?.toLowerCase())

        if(filteredSearchRedirects && filteredSearchRedirects.length === 1) {
            window.location.href = filteredSearchRedirects[0].target;
            return;
        }

        // If an item is highlighted, select it
        if (activeIndex >= 0) {
            if (filteredOptions && activeIndex < filteredOptions.length) {
                handleOptionSelect(filteredOptions[activeIndex].value);
            } else {
                const extIdIndex = activeIndex - filteredOptions.length;
                if (extIdIndex >= 0 && filteredExtIdItems && extIdIndex < filteredExtIdItems.length) {
                    handleExtIdClick(filteredExtIdItems[extIdIndex]);
                }
            }
            return;
        }

        // If no item is highlighted but there's exactly one result, select it
        if (filteredOptions.length > 0) {
            handleOptionSelect(filteredOptions[0].value);
            return;
        }
        if (filteredExtIdItems && filteredExtIdItems.length > 0) {
            handleExtIdClick(filteredExtIdItems[0]);
            return;
        }

        // If there are selected items, trigger search
        if (selectedOptions.length > 0) {
            setIsOpen(false);
        }
    }

    const handleInputChange = (e: Event) => {
        const target = e.target as HTMLInputElement

        // Handle keyboard navigation
        if (e instanceof KeyboardEvent) {
            const totalItems = filteredOptions.length + (filteredExtIdItems?.length || 0);

            if (e.key === 'ArrowDown') {
                e.preventDefault();

                // Wenn Dropdown geschlossen ist und Input leer, öffne es und springe zum ersten Element
                if (!isOpen && target.value.length === 0) {
                    setIsOpen(true);
                    setActiveIndex(0);
                    return;
                }

                // Wenn bereits getippt wurde und activeIndex noch -1 ist, starte bei Index 1 (zweites Element)
                if (activeIndex === -1 && target.value.length > 0) {
                    setActiveIndex(1);
                    return;
                }

                // Normal weitergehen
                setActiveIndex(prev => (prev < totalItems - 1 ? prev + 1 : prev));
                return;
            }

            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => (prev > 0 ? prev - 1 : -1));
                return;
            }

            if (e.key === 'Enter') {
                e.preventDefault();
                handleEnterAction(target?.value);
                return;
            }

            if (e.key === 'Backspace' && target.value.length === 0 && selectedOptions.length > 0) {
                // Remove last selected option
                handleOptionRemove(selectedOptions[selectedOptions.length - 1].value);
                return;
            }

            if (e.key === 'Escape') {
                setIsOpen(false);
                setActiveIndex(-1);
                return;
            }
        }

        if(target.value.length === 0) {
            if(isSearchWidget) {
                setIsFocused(true)
                setIsTyping(false)
            } else {
                setIsFocused(false)
                setIsTyping(true)
            }
            setShowEmptyFieldButton(false)
        } else {
            setShowEmptyFieldButton(true)
            setIsFocused(false)
            setIsTyping(true)
        }
        setSearchTerm(target.value)
        setActiveIndex(-1) // Reset active index when typing
        setIsOpen(true)
    }

    const handleClickContainer = (selectedOptions: FilterOption[]) => {
        if(selectedOptions.length < maxSelectedOptionItems) {
            setIsOpen(true)
            inputRef.current?.focus()
            setShowPlaceholder(false)
        } else if(selectedOptions.length === maxSelectedOptionItems) {
            setIsOpen(false)
            setShowPlaceholder(false)
            inputRef.current?.focus()
        } else {
            setIsOpen(false)
        }
    }

    // Close dropdown when clicking outside - Shadow DOM compatible
    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchTerm('')
            }
        }

        // For Shadow DOM compatibility, listen on both document and shadow root
        const shadowRoot = containerRef.current?.getRootNode() as ShadowRoot | Document

        if (shadowRoot && shadowRoot !== document) {
            // We're in a Shadow DOM
            shadowRoot.addEventListener('mousedown', handleClickOutside)
            return () => shadowRoot.removeEventListener('mousedown', handleClickOutside)
        } else {
            // Regular DOM
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    /*
        dynamic search input width
        How it works:
        The spanRef holds a hidden span that mirrors the input text.
        Whenever value changes, we measure the offsetWidth of the span and update the input's width dynamically.
        Tailwind handles styling (transition-all makes the width expand smoothly)
    */

    useEffect(() => {
        if (spanRef.current && inputRef.current) {
            if (selectedOptions.length > 0 && searchTerm.length > 0) {
                const newWidth = Math.max(spanRef.current.offsetWidth + 20, 10); // minimum 80px, padding buffer 20px
                inputRef.current.style.width = `${newWidth}px`;
            } else if (selectedOptions.length > 0 && searchTerm.length === 0) {
                // Minimale Breite wenn bereits Optionen ausgewählt sind
                inputRef.current.style.width = '4px';
            } else {
                // Volle Breite wenn keine Optionen ausgewählt sind
                inputRef.current.style.width = 'calc(100% - 28px)';
            }
        }
    }, [searchTerm, selectedOptions.length]);

    // Auto-scroll aktives Element in Sicht
    useEffect(() => {
        if (activeItemRef.current && dropdownRef.current) {
            const dropdown = dropdownRef.current;
            const activeItem = activeItemRef.current;

            const dropdownRect = dropdown.getBoundingClientRect();
            const activeRect = activeItem.getBoundingClientRect();

            // Scrolle nach unten wenn Element außerhalb des sichtbaren Bereichs ist
            if (activeRect.bottom > dropdownRect.bottom) {
                dropdown.scrollTop += activeRect.bottom - dropdownRect.bottom;
            }
            // Scrolle nach oben wenn Element außerhalb des sichtbaren Bereichs ist
            else if (activeRect.top < dropdownRect.top) {
                dropdown.scrollTop -= dropdownRect.top - activeRect.top;
            }
        }
    }, [activeIndex]);

    function handleInputBlur(selectedOptions: FilterOption[]) {
        // Verzögerung, damit onClick-Events der Buttons noch verarbeitet werden können
        setTimeout(() => {
            if(selectedOptions.length === 0) {
                setShowPlaceholder(true)
            }
        }, 150)
    }

    function handleExtIdClick(extIdItem: ExtIdItem) {
        if (extIdItem.url) {
            try {
                // Ensure URL is properly formatted
                let targetUrl = extIdItem.url;

                // If it's a relative URL, make it absolute
                if (!targetUrl.startsWith('http://') && !targetUrl.startsWith('https://')) {
                    targetUrl = window.location.origin + (targetUrl.startsWith('/') ? '' : '/') + targetUrl;
                }

                window.location.href = targetUrl;
            } catch (error) {
                console.error('Error redirecting to URL:', extIdItem.url, error);
                // Fallback: try the original URL anyway
                window.location.href = extIdItem.url;
            }
        }
    }

    function highlightMatch(text: string, searchTerm: string) {
        if (!searchTerm) return text;

        const normalizedSearchTerm = normalizeString(searchTerm.toLowerCase() || '')
        const normalizedText = normalizeString(text.toLowerCase() || '')
        const index = normalizedText.indexOf(normalizedSearchTerm);
        if (index === -1) return text;

        const before = text.slice(0, index);
        const match = text.slice(index, index + searchTerm.length);
        const after = text.slice(index + searchTerm.length);

        return (
            <>
                {before}
                <strong className="font-bold">{match}</strong>
                {after}
            </>
        );
    }

    function hideOverlayWhenFiltersEmpty(filteredExtIdItems : ExtIdItem[], filteredOptions : FilterOption[]) {
        let cssClass = '';
        if(isTyping) {
            if(
                filteredExtIdItems.length === 0 &&
                filteredOptions.length === 0
            ) {
                cssClass = 'hidden'
            }
        }
        return cssClass;
    }

    function resetInput() {
        if (inputRef.current) {
            //inputRef.current.focus();
            inputRef.current.value = '';
        }
        setIsFocused(true)
        setIsTyping(false)
        setIsOpen(false)
        setShowEmptyFieldButton(false);
        setSearchTerm('')
        setActiveIndex(-1)
        setShowPlaceholder(true)
    }

    return (
        <div className={`destination-search relative ${isSearchWidget ? 'search-widget' : ''} ${cssClasses}`} ref={containerRef}>
            <div className="relative">
                <div
                    className="destination-search-inner
                    flex items-start gap-2
                    w-full
                    px-[18px] lg:px-6
                    py-[7px] lg:py-[12px]
                    min-h-[50px] lg:min-h-[64px]
                    rounded-20px
                    border-2 border-theme-gray-light
                    cursor-text hover:cursor-pointer
                    "
                    onClick={() => handleClickContainer(selectedOptions)}
                    onFocus={() => handleClickContainer(selectedOptions)}
                    tabIndex={0}
                    aria-label="Destination Search Container"
                >
                    <IconLocationPin cssClasses="w-[22px] mt-1.5" />

                    <div className="flex-1 flex flex-wrap gap-2 items-start">

                        <div className="selection-wrapper flex-1 flex flex-wrap gap-x-1 lg:gap-1 items-start">
                            {/* Selected items as tags */}
                            {selectedOptions?.map(option => (
                                <div
                                    key={option.value}
                                    className="selection"
                                    tabIndex={0}
                                    aria-label={`Auswahl ${option.value}`}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleOptionRemove(option.value)
                                    }}
                                >
                                    <span className="inline-flex">{option.value}</span>
                                    <button
                                        className="close p-2 w-auto rounded-full flex items-center justify-center text-md cursor-pointer"
                                    >
                                        <IconCloseBig cssClasses={`${isSearchWidget ? 'w-2.5 lg:w-4' : 'w-2'} mt-0.5`}/>
                                    </button>
                                </div>
                            ))}

                            <input
                                autocomplete="off"
                                ref={inputRef}
                                name="destination_search"
                                type="text"
                                inputmode="search"
                                enterkeyhint="search"
                                value={searchTerm}
                                onClick={() => setIsOpen(!isOpen)}
                                onChange={handleInputChange}
                                onInput={handleInputChange}
                                onKeyDown={handleInputChange}
                                onFocus={(e: Event) => handleFocusChange(e, selectedOptions)}
                                onBlur={() => handleInputBlur(selectedOptions)}
                                placeholder={(selectedOptions.length == 0 && showPlaceholder) ? placeholder : ''}
                                style={`${selectedOptions.length == 0 ? ' width: calc(100% - 38px)' : ' width: 4px'};`}
                                disabled={selectedOptions.length > maxSelectedOptionItems}
                                className={`
                                        lg:w-full
                                        text-[16px] lg:text-[18px]
                                        outline-none bg-transparent font-reckless
                                        placeholder:text-theme-black
                                        h-8
                                        hover:cursor-pointer
                                        ${selectedOptions.length >= maxSelectedOptionItems ? 'absolute w-0! left-[-9999em] max-w-0 px-0' : ''}
                                    `}
                            />
                            {(showEmptyFieldButton && selectedOptions.length === 0) && (
                                <button className="p-2 pr-0 cursor-pointer"
                                        onClick={resetInput}>
                                    <IconCloseBig cssClasses="w-2.5 lg:w-4 mt-0.5"/>
                                </button>
                            )}
                            <span
                                ref={spanRef}
                                class="absolute invisible whitespace-pre text-sm px-2 py-1 font-sans"
                            >
                            {searchTerm || " "}
                          </span>
                        </div>
                    </div>
                </div>

                {/* Dropdown */}
                {isOpen && (
                    <div
                        ref={dropdownRef}
                        className={`
                             destination-search-popover
                             absolute top-full left-0 pt-2.5 right-0 mt-2 bg-white
                             rounded-10px shadow-filter-box overflow-y-auto z-50
                             max-h-[360px]
                             ${hideOverlayWhenFiltersEmpty(filteredExtIdItems as ExtIdItem[], filteredOptions)}
                        `}
                        tabIndex={0}
                        aria-label="Dropdown Container"
                    >

                        {isFocused && (
                            <>
                                {kuratierteReisen && kuratierteReisen?.length > 0 && (
                                    <div className="px-7 pb-[30px] pt-5">
                                        <div className="font-reckless font-bold text-16px mb-5">Beliebte Reiseziele</div>
                                        <div className={`flex flex-wrap gap-2`}>
                                            {kuratierteReisen?.map((option, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleOptionSelect(option.value)}
                                                    tabIndex={0}
                                                    className="
                                                    inline-flex items-center
                                                    border-2 border-solid border-theme-gray-light
                                                    rounded-10px
                                                    py-2 px-4
                                                    text-16px
                                                    font-reckless
                                                    text-theme-black
                                                    leading-[1.2em]
                                                    transition duration-200 ease-linear
                                                    hover:font-semibold
                                                    hover:bg-theme-hintergrund
                                                    hover:border-theme-hintergrund
                                                    hover:cursor-pointer
                                                    focus:font-semibold
                                                    focus:bg-theme-hintergrund
                                                ">
                                                    {option.value}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {isTyping && (
                            <>
                                {filteredExtIdItems?.length === 1 && (
                                    <ul className="pb-2.5">
                                        {filteredExtIdItems.map((extIdItem, index) => {
                                            const isActive = activeIndex === index;
                                            const setFirstActiveColor = (index === 0 && activeIndex === -1) ? 'bg-theme-red-light' : '';
                                            return (
                                                <li key={extIdItem.extid}>
                                                    <button
                                                        ref={isActive ? activeItemRef : null}
                                                        onClick={() => handleExtIdClick(extIdItem)}
                                                        tabIndex={0}
                                                        className={`w-full flex justify-between items-center px-4 py-2 font-reckless text-left hover:bg-theme-red-light transition-colors ${setFirstActiveColor ? 'bg-theme-red-light' : ''} ${isActive ? 'bg-theme-red-light' : ''}`}
                                                    >
                                                        {extIdItem.extid}
                                                    </button>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                )}

                                {/* FilteredOptions werden zuerst gerendert */}
                                {filteredOptions.length > 0 && (
                                    <ul className="pb-2.5">
                                        {filteredOptions.slice(0,maxFilteredOptionItems).map((option, index) => {
                                            const isActive = activeIndex === index;
                                            // Nur das erste Element (index 0) wird aktiv markiert wenn activeIndex === -1
                                            const setFirstActiveColor = (index === 0 && activeIndex === -1) ? 'bg-theme-red-light' : '';
                                            return (
                                                <li key={option.value}>
                                                    <button
                                                        ref={isActive ? activeItemRef : null}
                                                        onClick={() => handleOptionSelect(option.value)}
                                                        tabIndex={0}
                                                        className={`
                                                            w-full flex justify-between items-center px-4 py-2 font-reckless text-left hover:bg-theme-red-light transition-colors
                                                            ${isActive ? 'bg-theme-red-light' : setFirstActiveColor}
                                                        `}
                                                    >
                                                        <span>{highlightMatch(option.value, searchTerm)}</span>
                                                        {option.count ? (<span className="font-montserrat text-[12px] text-theme-gray-dark">{option.count} {option.count === 1 ? 'Reise' : 'Reisen'}</span>) : ('')}
                                                    </button>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="absolute h-[15px] lg:h-[18px] top-0 -mt-[15px] lg:-mt-[17px] left-7 inline-flex bg-white px-2 py-0 lg:p-2 lg:py-0 text-[10px] lg:text-[12px] tracking-[1px] translate-y-1/2">
                {label}
            </div>
        </div>
    )
}